import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ChevronLeft, FileText, CheckCircle, XCircle, Clock, ExternalLink,
  User, Mail, Phone, Calendar, BookOpen, Award,
} from 'lucide-react';
import { fetchFileAsObjectUrl, isFileKey } from '../utils/fileHelpers';

const TABS = ['Overview', 'Documents', 'Attendance'];

const STATUS_CONFIG = {
  pending:  { label: 'Pending',  cls: 'bg-gray-100 text-gray-600',   icon: Clock },
  verified: { label: 'Verified', cls: 'bg-green-100 text-green-700', icon: CheckCircle },
  failed:   { label: 'Failed',   cls: 'bg-red-100 text-red-700',     icon: XCircle },
};

export default function AdminStudentProfilePage() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState('Overview');
  const [profile, setProfile] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [docUrls, setDocUrls] = useState({});
  const [updatingDoc, setUpdatingDoc] = useState(null);
  const [updatingAttendance, setUpdatingAttendance] = useState(null);
  const [docNotes, setDocNotes] = useState({});

  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/students/${studentId}/profile`, auth());
      setProfile(res.data);
      const notes = {};
      (res.data.documents || []).forEach(d => { notes[d.key] = d.notes || ''; });
      setDocNotes(notes);
    } catch {
      toast.error('Failed to load student profile');
    } finally {
      setLoading(false);
    }
  }, [studentId, auth]);

  const fetchAttendance = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/students/${studentId}/attendance`, auth());
      setAttendance(res.data);
    } catch {
      toast.error('Failed to load attendance');
    }
  }, [studentId, auth]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  useEffect(() => {
    if (tab === 'Attendance') fetchAttendance();
  }, [tab, fetchAttendance]);

  // Resolve presigned URLs for documents
  useEffect(() => {
    if (!profile?.documents) return;
    profile.documents.forEach(doc => {
      if (doc.file_key && isFileKey(doc.file_key) && !docUrls[doc.key]) {
        fetchFileAsObjectUrl(doc.file_key).then(url => {
          if (url) setDocUrls(prev => ({ ...prev, [doc.key]: url }));
        });
      }
    });
  }, [profile]);

  const handleDocVerification = async (docKey, status) => {
    setUpdatingDoc(docKey);
    try {
      await axios.patch(
        `${API}/students/${studentId}/documents/${docKey}`,
        { status, notes: docNotes[docKey] || '' },
        auth(),
      );
      toast.success('Document status updated');
      fetchProfile();
    } catch {
      toast.error('Failed to update document');
    } finally {
      setUpdatingDoc(null);
    }
  };

  const handleWithdraw = async () => {
    if (!profile.application_id) { toast.error('No linked application found'); return; }
    if (!window.confirm(`Mark ${profile.full_name} as Withdrawn? This will remove them from their cohort.`)) return;
    try {
      await axios.patch(`${API}/applications/${profile.application_id}/withdraw`, {}, auth());
      toast.success(`${profile.full_name} marked as withdrawn`);
      navigate(backTo);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to withdraw student');
    }
  };

  const handleAttendanceToggle = async (sessionId, currentPresent) => {
    setUpdatingAttendance(sessionId);
    const newPresent = currentPresent === true ? false : true;
    try {
      await axios.patch(
        `${API}/sessions/${sessionId}/attendance/${studentId}`,
        { present: newPresent },
        auth(),
      );
      setAttendance(prev => prev.map(s =>
        s.session_id === sessionId ? { ...s, present: newPresent } : s
      ));
    } catch {
      toast.error('Failed to update attendance');
    } finally {
      setUpdatingAttendance(null);
    }
  };

  const backTo = location.state?.from || '/admin/cohorts';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">Loading…</div>
    );
  }

  if (!profile) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <p className="text-muted-foreground">Student not found</p>
        <Button variant="outline" size="sm" onClick={() => navigate(backTo)}>Go Back</Button>
      </div>
    );
  }

  const attended = attendance.filter(s => s.present === true).length;
  const total = attendance.length;
  const pct = total > 0 ? Math.round((attended / total) * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-50/50">

      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" onClick={() => navigate(backTo)}>
            <ChevronLeft className="w-4 h-4" /> Back
          </Button>
          <div className="h-4 w-px bg-border" />
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-playfair font-bold text-primary truncate">{profile.full_name}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              {profile.cohort && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                  {profile.cohort.name}
                </span>
              )}
              <span className="text-xs text-muted-foreground">{profile.email}</span>
            </div>
          </div>
          {profile.application_id && (
            <Button variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50 gap-1.5 shrink-0" onClick={handleWithdraw}>
              <XCircle className="w-3.5 h-3.5" /> Mark as Withdrawn
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b px-6 flex gap-1">
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="p-6 max-w-4xl">

        {/* ── Overview ──────────────────────────────────────────────────────── */}
        {tab === 'Overview' && (
          <div className="space-y-4">
            <Card className="shadow-sm border-0 ring-1 ring-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold">Student Information</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                {[
                  { icon: User,     label: 'Full Name',       value: profile.full_name },
                  { icon: Mail,     label: 'Email',           value: profile.email },
                  { icon: Phone,    label: 'Phone',           value: profile.phone || '—' },
                  { icon: Calendar, label: 'Enrolled',        value: profile.enrollment_date ? new Date(profile.enrollment_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—' },
                  { icon: BookOpen, label: 'Cohort',          value: profile.cohort?.name || '—' },
                  { icon: Award,    label: 'Orientation RSVP',value: profile.orientation_rsvp || 'pending' },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex items-start gap-3">
                    <Icon className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">{label}</p>
                      <p className="text-sm font-medium capitalize">{value}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="shadow-sm border-0 ring-1 ring-gray-200 text-center p-4">
                <p className="text-2xl font-bold text-primary">{profile.documents?.length || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">Documents submitted</p>
              </Card>
              <Card className="shadow-sm border-0 ring-1 ring-gray-200 text-center p-4">
                <p className="text-2xl font-bold text-green-600">
                  {profile.documents?.filter(d => d.status === 'verified').length || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Documents verified</p>
              </Card>
              <Card className="shadow-sm border-0 ring-1 ring-gray-200 text-center p-4">
                <p className="text-2xl font-bold text-primary">
                  {profile.attendance_summary?.sessions_attended ?? 0}/{profile.attendance_summary?.sessions_total ?? 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Sessions attended</p>
              </Card>
            </div>
          </div>
        )}

        {/* ── Documents ─────────────────────────────────────────────────────── */}
        {tab === 'Documents' && (
          <div className="space-y-3">
            {profile.documents.length === 0 && (
              <Card className="shadow-sm border-0 ring-1 ring-gray-200">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No documents submitted</p>
                </CardContent>
              </Card>
            )}
            {profile.documents.map(doc => {
              const cfg = STATUS_CONFIG[doc.status] || STATUS_CONFIG.pending;
              const Icon = cfg.icon;
              const url = docUrls[doc.key] || (doc.file_key && !isFileKey(doc.file_key) ? doc.file_key : null);
              return (
                <Card key={doc.key} className="shadow-sm border-0 ring-1 ring-gray-200">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                          <p className="font-medium text-sm">{doc.label}</p>
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${cfg.cls}`}>
                            <Icon className="w-3 h-3" /> {cfg.label}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground truncate mb-3">{doc.filename}</p>

                        {/* Notes */}
                        <div className="mb-3">
                          <input
                            type="text"
                            placeholder="Add a note (optional)…"
                            value={docNotes[doc.key] || ''}
                            onChange={e => setDocNotes(prev => ({ ...prev, [doc.key]: e.target.value }))}
                            className="w-full text-sm border rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/20"
                          />
                        </div>

                        {/* Verified by / at */}
                        {doc.status === 'verified' && doc.verified_by && (
                          <p className="text-xs text-muted-foreground">
                            Verified by {doc.verified_by}
                            {doc.verified_at && ` · ${new Date(doc.verified_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                          </p>
                        )}
                        {doc.status === 'failed' && doc.notes && (
                          <p className="text-xs text-red-600">Note: {doc.notes}</p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        {url && (
                          <a href={url} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                            <ExternalLink className="w-3 h-3" /> View
                          </a>
                        )}
                        <div className="flex gap-1.5">
                          <Button size="sm" variant="outline"
                            className={`h-7 text-xs ${doc.status === 'verified' ? 'bg-green-50 border-green-300 text-green-700' : ''}`}
                            disabled={updatingDoc === doc.key}
                            onClick={() => handleDocVerification(doc.key, 'verified')}>
                            <CheckCircle className="w-3 h-3 mr-1" /> Verify
                          </Button>
                          <Button size="sm" variant="outline"
                            className={`h-7 text-xs ${doc.status === 'failed' ? 'bg-red-50 border-red-300 text-red-700' : ''}`}
                            disabled={updatingDoc === doc.key}
                            onClick={() => handleDocVerification(doc.key, 'failed')}>
                            <XCircle className="w-3 h-3 mr-1" /> Fail
                          </Button>
                          {doc.status !== 'pending' && (
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground"
                              disabled={updatingDoc === doc.key}
                              onClick={() => handleDocVerification(doc.key, 'pending')}>
                              Reset
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* ── Attendance ────────────────────────────────────────────────────── */}
        {tab === 'Attendance' && (
          <div className="space-y-4">
            {/* Summary bar */}
            <Card className="shadow-sm border-0 ring-1 ring-gray-200">
              <CardContent className="p-4 flex items-center gap-6">
                <div>
                  <p className="text-2xl font-bold text-primary">{pct}%</p>
                  <p className="text-xs text-muted-foreground">Attendance rate</p>
                </div>
                <div className="flex-1">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{attended} of {total} sessions attended</p>
                </div>
              </CardContent>
            </Card>

            {/* Session list */}
            {attendance.length === 0 ? (
              <Card className="shadow-sm border-0 ring-1 ring-gray-200">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Calendar className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No sessions recorded yet</p>
                  <p className="text-xs mt-1">Add sessions from the Cohorts page</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {attendance.map(s => {
                  const isPresent = s.present === true;
                  const isAbsent  = s.present === false;
                  const isLoading = updatingAttendance === s.session_id;
                  return (
                    <Card key={s.session_id} className="shadow-sm border-0 ring-1 ring-gray-200">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{s.label}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(s.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                            {s.marked_by && ` · Marked by ${s.marked_by}`}
                          </p>
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                          <Button size="sm" variant="outline" disabled={isLoading}
                            className={`h-7 text-xs ${isPresent ? 'bg-green-50 border-green-300 text-green-700' : ''}`}
                            onClick={() => handleAttendanceToggle(s.session_id, s.present)}>
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {isPresent ? 'Present' : 'Mark Present'}
                          </Button>
                          {isPresent && (
                            <Button size="sm" variant="ghost" disabled={isLoading}
                              className="h-7 text-xs text-muted-foreground"
                              onClick={() => handleAttendanceToggle(s.session_id, true)}>
                              Undo
                            </Button>
                          )}
                          {s.present === null && (
                            <span className="text-xs text-muted-foreground italic self-center">Not marked</span>
                          )}
                          {isAbsent && !isPresent && (
                            <span className="text-xs text-red-500 font-medium self-center">Absent</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
